import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { listSaved, type SavedItem } from "@/lib/account.functions";
import { BookMarked } from "lucide-react";

export const Route = createFileRoute("/_authenticated/saved")({
  head: () => ({ meta: [{ title: "Saved Discoveries - Smartasaurus" }, { name: "robots", content: "noindex" }] }),
  component: SavedPage,
});

function SavedPage() {
  const fetch = useServerFn(listSaved);
  const [items, setItems] = useState<SavedItem[] | null>(null);
  useEffect(() => { fetch().then((r) => setItems(r.items)).catch(() => setItems([])); }, [fetch]);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main-content" className="mx-auto max-w-7xl px-4 py-16 md:px-8 md:py-24">
        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">· Library</div>
        <h1 className="mt-3 font-display text-5xl uppercase leading-[0.95] md:text-7xl">Saved <span className="text-gradient-curiosity">discoveries</span></h1>

        {items === null && <p className="mt-6 text-sm text-muted-foreground">Loading…</p>}
        {items?.length === 0 && (
          <div className="mt-10 overflow-hidden rounded-2xl border border-border bg-card">
            <div className="bg-gradient-to-br from-primary/10 via-card to-card p-8 text-center">
              <BookMarked className="mx-auto h-8 w-8 text-primary" />
              <h2 className="mt-3 font-display text-2xl uppercase leading-tight md:text-3xl">
                Your library is empty - for now.
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Tap the bookmark on any discovery to keep it here. Then come back any time to read or share it.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 border-t border-border p-5 sm:grid-cols-3">
              {[
                { to: "/discoveries" as const, title: "Browse Discoveries", note: "The full curiosity feed.", cta: "Explore →" },
                { to: "/collections" as const, title: "Open a Collection", note: "Themed deep-dives, episode by episode.", cta: "Pick one →" },
                { to: "/play" as const, title: "Play", note: "Test what you know. Earn XP.", cta: "Start →" },
              ].map((r) => (
                <Link
                  key={r.to}
                  to={r.to}
                  className="group rounded-xl border border-border bg-background p-4 text-left transition hover:border-primary/40"
                >
                  <div className="font-display text-base uppercase leading-tight group-hover:text-primary">{r.title}</div>
                  <p className="mt-1 text-xs text-muted-foreground">{r.note}</p>
                  <div className="mt-3 text-[10px] font-bold uppercase tracking-widest text-primary">{r.cta}</div>
                </Link>
              ))}
            </div>
          </div>
        )}
        {items && items.length > 0 && (
          <div className="mt-10 grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3">
            {items.map((it) => (
              <Link key={it.discovery_id} to="/discovery/$slug" params={{ slug: it.slug }} className="group overflow-hidden rounded-2xl border border-border bg-card transition hover:border-primary/40">
                {it.image_url && <img src={it.image_url} alt={it.title} loading="lazy" className="aspect-[5/3] w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                <div className="p-3 md:p-4">
                  {it.category_label && <div className="text-[10px] font-bold uppercase tracking-widest text-primary">{it.category_label}</div>}
                  <div className="mt-1 line-clamp-2 font-display text-sm uppercase leading-tight md:text-lg">{it.title}</div>
                  <div className="mt-2 hidden line-clamp-2 text-xs text-muted-foreground sm:block">{it.excerpt}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
